import { useNavigate } from "react-router-dom";
import BellAlertIcon from "./../../assets/icons/bell-alert.svg?react";
import CloseIcon from "./../../assets/icons/close.svg?react";
import ArrowRightIcon from "./../../assets/icons/arrow-right.svg?react";
import { BlockEventNotificationsUrl } from "../../utils/urls";
import { useEffect, useMemo, useState } from "react";
import { BlockEventCreateLevels } from "../../types/block-events";
import SelectTempleteStep from "../../components/block-events/notifications/create-page-steps/SelectTempleteStep";
import CreateExpressionStep from "../../components/block-events/notifications/create-page-steps/CreateExpressionStep";
import SelectDestinationStep from "../../components/block-events/notifications/create-page-steps/SelectDestinationStep";
import { useAppDispatch, useAppSelector } from "../../hooks";
import {
  selectExpressionHasValidate,
  selectExpressionValue,
  selectNotificationName,
  selectSelectedChain,
  selectSelectedNetwork,
  selectSelectedTemplate,
  selectSelectedDestinations,
  createBlockEventAsync,
  selectCreateBlockEventLoading,
  handleClearBlockEventCreationSlice,
} from "../../store/block-event/blockEventCreateSlice";
import { Button, Steps, Typography } from "djuno-design";
import useConfigIntercomeSetting from "../../hooks/useConfigIntercomeSetting";
import useLimitations from "../../hooks/useLimitations";
import {
  getBlockEventsAsync,
  selectBlockEvents,
  // selectBlockEventsLoading,
} from "../../store/block-event/blockEventsSlice";
import { useScrollToElement } from "../../hooks/useScrollToElement";

const BlockEventCreatePage = () => {
  useConfigIntercomeSetting();
  const navigate = useNavigate();
  const [step, setStep] = useState<BlockEventCreateLevels>("SelectTemplete");

  const blockEvents = useAppSelector(selectBlockEvents);
  // const blockEventsLoading = useAppSelector(selectBlockEventsLoading);

  const notificationName = useAppSelector(selectNotificationName);
  const selectedChain = useAppSelector(selectSelectedChain);
  const selectedNetwork = useAppSelector(selectSelectedNetwork);
  const selectedTemplate = useAppSelector(selectSelectedTemplate);
  const expressionValue = useAppSelector(selectExpressionValue);
  const expressionHasValidate = useAppSelector(selectExpressionHasValidate);
  const destinations = useAppSelector(selectSelectedDestinations);
  const createLoading = useAppSelector(selectCreateBlockEventLoading);
  const dispatch = useAppDispatch();

  useEffect(() => {
    return () => {
      dispatch(handleClearBlockEventCreationSlice());
    };
  }, [dispatch]);

  useEffect(() => {
    if (blockEvents.length === 0) {
      dispatch(getBlockEventsAsync());
    }
  }, [dispatch]);

  const { numberOfBlockEventsLimit, numberOfBlockEventsLimitChecker } =
    useLimitations({
      cases: ["NumberOfBlockEvent"],
    });

  const isExceedNumberOfBlockEvent = useMemo(() => {
    return numberOfBlockEventsLimitChecker(blockEvents.length);
  }, [blockEvents, numberOfBlockEventsLimitChecker]);

  useScrollToElement({ dependency: step });

  return (
    <>
      <div className="flex items-center justify-between h-16 px-6 sticky top-0 z-20 bg-white dark:bg-dark-1 border-b dark:border-dark-2">
        <div className="items-center justify-between flex flex-1 transition duration-150">
          <div className="dark:text-slate-100 flex items-center gap-1">
            <BellAlertIcon className="w-6 h-6" />
            <Typography.Text className="font-medium">
              Create Event
            </Typography.Text>
          </div>
        </div>
        <div className="">
          <Button
            uiType="light"
            uiSize="small"
            onClick={() => navigate(BlockEventNotificationsUrl)}
            className="group"
          >
            <CloseIcon className="w-3 h-3 group-hover:rotate-90 group-hover:scale-110 transition-all duration-500" />
          </Button>
        </div>
      </div>

      <Steps
        step={step}
        steps={[
          {
            label: "Select Templete",
            value: "SelectTemplete",
            callback: () => setStep("SelectTemplete"),
          },
          {
            label: "Create Expression",
            value: "CreateExpression",
            callback: () => setStep("CreateExpression"),
          },
          { label: "Select Destination", value: "SelectDestination" },
        ]}
        className="px-6"
      />
      <div className="mt-5 w-full px-6">
        <div className="mx-auto flex-1 flex justify-start pb-14 w-full">
          {step === "SelectTemplete" && <SelectTempleteStep />}
          {step === "CreateExpression" && <CreateExpressionStep />}
          {step === "SelectDestination" && <SelectDestinationStep />}
        </div>
      </div>
      <div className="fixed bottom-0 right-0 left-0 flex items-center w-full h-16 border-t bg-white dark:bg-dark-1 dark:border-dark-2 px-6 lg:pl-80">
        {step === "SelectTemplete" && (
          <div className="flex justify-end w-full">
            <Button
              uiType="primary"
              disabled={
                !notificationName ||
                !selectedChain ||
                !selectedNetwork ||
                !selectedTemplate
              }
              onClick={() => setStep("CreateExpression")}
              className="group  w-[110px]"
            >
              Continue
              <ArrowRightIcon className="w-4 h-4 group-hover:scale-110 group-hover:translate-x-1 transition-all duration-300" />
            </Button>
          </div>
        )}
        {step === "CreateExpression" && (
          <div className="flex items-center justify-between w-full">
            <Button
              uiType="light"
              onClick={() => {
                setStep("SelectTemplete");
              }}
            >
              back
            </Button>
            <Button
              uiType="primary"
              disabled={
                !expressionValue || !expressionHasValidate || !selectedNetwork
              }
              onClick={() => setStep("SelectDestination")}
              className="group  w-[110px]"
            >
              Continue
              <ArrowRightIcon className="w-4 h-4 group-hover:scale-110 group-hover:translate-x-1 transition-all duration-300" />
            </Button>
          </div>
        )}
        {step === "SelectDestination" && (
          <div className="flex items-center justify-between w-full">
            <Button
              uiType="light"
              onClick={() => {
                setStep("CreateExpression");
              }}
            >
              back
            </Button>
            <Button
              uiType="primary"
              disabled={destinations.length === 0 || isExceedNumberOfBlockEvent}
              onClick={() => {
                dispatch(createBlockEventAsync()).then((action) => {
                  if (action.type === "block-events/create/fulfilled") {
                    navigate(BlockEventNotificationsUrl);
                  }
                });
              }}
              className="group  w-[110px]"
              loading={createLoading}
              tooltip={{
                content: isExceedNumberOfBlockEvent
                  ? numberOfBlockEventsLimit?.LimitationMessage
                  : "",
                className: "!text-sm",
              }}
            >
              Deploy
            </Button>
          </div>
        )}
      </div>
    </>
  );
};

export default BlockEventCreatePage;
